import { FC, ReactElement, ReactNode, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { cn, FaIcon } from '@fe-learner-common';

export interface SheetProps extends Dialog.DialogProps {
  children: ReactNode;
  sheetClassName?: string;
  trigger: ReactElement;
  open?: boolean;
  slideDirection?: 'ltr' | 'rtl';
  onOpenChange?: (open: boolean) => void;
  onCloseAutoFocus?: (event: Event) => void;
  headless?: boolean;
  preventDismiss?: boolean;
  container?: HTMLElement;
  texts?: {
    title: string;
    closeAriaLabel: string;
  };
}

const directionMap = {
  rtl: 'data-[state=closed]:animate-sheetContentSlideOutRtl data-[state=open]:animate-sheetContentSlideInRtl rtl:data-[state=closed]:animate-sheetContentSlideOut rtl:data-[state=open]:animate-sheetContentSlideIn',
  ltr: 'data-[state=closed]:animate-sheetContentSlideOut data-[state=open]:animate-sheetContentSlideIn rtl:data-[state=closed]:animate-sheetContentSlideOutRtl rtl:data-[state=open]:animate-sheetContentSlideInRtl',
};

export const Sheet: FC<SheetProps> = ({
  children,
  sheetClassName,
  texts,
  slideDirection = 'ltr',
  onCloseAutoFocus,
  trigger,
  container,
  headless = false,
  preventDismiss = false,
  ...rest
}) => {
  const blockAutoFocus = useRef(true);

  return (
    <Dialog.Root
      {...rest}
      onOpenChange={(open) => {
        if (open) {
          blockAutoFocus.current = true;
        }
        rest.onOpenChange && rest.onOpenChange(open);
      }}
    >
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal container={container}>
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-[10%] data-[state=open]:animate-sheetOverlayShow" />
        <Dialog.Content
          className={cn(
            'fixed bottom-0 end-0 top-0 mt-16 flex w-full flex-col bg-neutral-0 shadow-100',
            directionMap[slideDirection],
            sheetClassName,
          )}
          onEscapeKeyDown={(event) => {
            blockAutoFocus.current = false;

            if (preventDismiss) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            blockAutoFocus.current = false;

            if (preventDismiss) {
              event.preventDefault();
            }
          }}
          onCloseAutoFocus={(event) => {
            if (blockAutoFocus.current) {
              onCloseAutoFocus && onCloseAutoFocus(event);
            }
          }}
        >
          {headless ? null : (
            <div className="flex items-center justify-between border-b-1 border-neutral-100 p-3 ps-6">
              <Dialog.Title asChild>
                <h2 className="text-title-md">{texts?.title}</h2>
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  className="l-btn l-icon-btn l-btn-md l-btn-tertiary"
                  id="closeSheet"
                  key="closeSheet"
                  aria-label={texts?.closeAriaLabel}
                >
                  <FaIcon width={20} height={20} icon={faXmark} />
                </button>
              </Dialog.Close>
            </div>
          )}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
